import Phaser from 'phaser';
import Wall from '../objects/Wall';
import Door, { DoorDirection } from '../objects/Door';
import Chest from '../objects/Chest';
import Potion from '../objects/Potion';
import { FLOOR_FRAME } from '../tileFrames';
import { tr } from '../i18n';
import * as Bal from '../balance';
import { DEPTH, UI } from '../ui/theme';

export interface DungeonGroups {
  walls: Phaser.Physics.Arcade.StaticGroup;
  doors: Phaser.Physics.Arcade.StaticGroup;
  chests: Phaser.Physics.Arcade.StaticGroup;
  potions: Phaser.Physics.Arcade.Group;
}

/** 地面格个数配置（单一来源） */
const CHEST_COUNT = 2;
const POTION_COUNT = 2;
/** 门之间的最小曼哈顿距离系数（按 map 宽高） */
const DOOR_MIN_DIST_FACTOR = 1 / 3;

/** 房间生成配置 */
const ROOM_MIN_SIZE = 5;          // 房间最小宽/高（瓦片）
const ROOM_MAX_SIZE = 11;         // 房间最大宽/高
const ROOM_MIN_COUNT = 4;         // 最少房间数
const ROOM_MAX_COUNT = 12;        // 最多房间数
const CORRIDOR_WIDTH = 1;         // 走廊半宽（1=3格宽）
/** 房间内点缀石柱的密度（仅在不会阻断通行的位置放置） */
const ROOM_PILLAR_DENSITY = 0.03;

/** 房间矩形 */
interface Room {
  x: number; y: number; w: number; h: number;
  cx: number; cy: number; // 中心点
}

/** 一次生成得到的布局：开放格（可通行）+ 岩石墙格 + 房间列表 */
interface Layout {
  openTiles: Set<string>;
  wallTiles: Set<string>;
  rooms: Room[];
}

/**
 * 地牢生成器：负责一层的几何（地面/边界墙/房间+走廊/门/宝箱/药水）、
 * 世界与相机边界、出生点安全检测。
 * 独占持有 map 尺寸 / 地面贴图 / 门标签 / 下行门引用。
 */
export default class DungeonBuilder {
  private w = 0;
  private h = 0;
  private floorTile?: Phaser.GameObjects.TileSprite;
  private doorLabels: Phaser.GameObjects.Text[] = [];
  /** 当前一层的可行走开放格（房间 ∪ 走廊，已扣除石柱）——用于刷怪位置校验 */
  private openTiles = new Set<string>();
  downDoor?: Door;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly groups: DungeonGroups
  ) {}

  get width() {
    return this.w;
  }
  get height() {
    return this.h;
  }
  /** 世界像素尺寸 */
  get worldWidth() {
    return this.w * Bal.TILE;
  }
  get worldHeight() {
    return this.h * Bal.TILE;
  }

  tileKey(x: number, y: number): string {
    return `${x},${y}`;
  }

  /** 瓦片中心的世界像素坐标（与玩家/敌人/地板网格保持一致） */
  private px(t: number): number {
    return t * Bal.TILE + Bal.TILE / 2;
  }

  /** 生成一层地牢，返回地图瓦片尺寸 */
  build(floor: number): { w: number; h: number } {
    const { w, h } = Bal.computeMapSize(floor);
    this.w = w;
    this.h = h;
    const worldW = w * Bal.TILE;
    const worldH = h * Bal.TILE;

    this.scene.physics.world.setBounds(0, 0, worldW, worldH);
    this.scene.cameras.main.setBounds(0, 0, worldW, worldH);

    this.doorLabels.forEach((t) => t.destroy());
    this.doorLabels = [];
    this.downDoor = undefined;

    this.floorTile?.destroy();
    this.floorTile = this.scene.add
      .tileSprite(0, 0, worldW, worldH, 'tileset', FLOOR_FRAME)
      .setOrigin(0)
      .setDepth(DEPTH.floor);

    // ── ① 先生成「房间 + 走廊」布局：得到开放格（可通行）与岩石墙格 ──
    const { openTiles, wallTiles, rooms } = this.generateLayout(floor);

    // 候选池：优先房间内的开放格（四周空间充足、视觉自然），否则回退到任意开放格
    const roomSet = new Set<string>();
    for (const r of rooms) {
      for (let dy = 0; dy < r.h; dy++) {
        for (let dx = 0; dx < r.w; dx++) {
          const k = this.tileKey(r.x + dx, r.y + dy);
          if (openTiles.has(k)) roomSet.add(k);
        }
      }
    }
    const roomList = Array.from(roomSet);
    const openList = Array.from(openTiles);

    // ── ② 在开放格上放置门 / 宝箱 / 药水（保证可达，绝不被岩石包死）──
    const reserved = new Set<string>();
    const doorPlan: { x: number; y: number; dir: DoorDirection }[] = [];

    // 下行门：从房间内开放格中挑选（pickOpenTile 已优先房间池）
    const downPick = this.pickOpenTile(roomList, openList, reserved);
    reserved.add(this.tileKey(downPick.x, downPick.y));
    doorPlan.push({ x: downPick.x, y: downPick.y, dir: 'down' });

    // 上行门：尽量远离下行门
    if (floor > 1) {
      const minDist = Math.floor((w + h) * DOOR_MIN_DIST_FACTOR);
      const upPick = this.pickOpenTile(roomList, openList, reserved, downPick, minDist);
      reserved.add(this.tileKey(upPick.x, upPick.y));
      doorPlan.push({ x: upPick.x, y: upPick.y, dir: 'up' });
    }

    const chestPlan: { x: number; y: number }[] = [];
    for (let i = 0; i < CHEST_COUNT; i++) {
      const t = this.pickOpenTile(roomList, openList, reserved);
      reserved.add(this.tileKey(t.x, t.y));
      chestPlan.push(t);
    }
    const potionPlan: { x: number; y: number }[] = [];
    for (let i = 0; i < POTION_COUNT; i++) {
      const t = this.pickOpenTile(roomList, openList, reserved);
      reserved.add(this.tileKey(t.x, t.y));
      potionPlan.push(t);
    }

    // ── ③ 房间内点缀石柱（避开已占位，绝不阻断通行）──
    this.addRoomPillars(rooms, openTiles, wallTiles, ROOM_PILLAR_DENSITY, reserved);

    // 记录本层可行走开放格（石柱已从 openTiles 移除），供刷怪位置校验使用
    this.openTiles = openTiles;

    // ── ④ 边界墙（四角不重复）──
    for (let x = 0; x < w; x++) {
      this.createWall(x, 0);
      this.createWall(x, h - 1);
    }
    for (let y = 1; y < h - 1; y++) {
      this.createWall(0, y);
      this.createWall(w - 1, y);
    }

    // ── ⑤ 内部岩石墙 ──
    for (const key of wallTiles) {
      const [gx, gy] = key.split(',').map(Number);
      this.createWall(gx, gy);
    }

    // ── ⑥ 内容 ──
    doorPlan.forEach((d) => this.createDoor(d.x, d.y, d.dir));
    chestPlan.forEach((c) => this.createChest(c.x, c.y));
    potionPlan.forEach((p) => this.createPotion(p.x, p.y));

    return { w, h };
  }

  /** 下行门上锁 / 解锁的视觉状态（上锁时半透明） */
  setDoorLocked(locked: boolean) {
    if (!this.downDoor) return;
    this.downDoor.setAlpha(locked ? 0.55 : 1);
  }

  /** 含墙/门/宝箱/药水的不可生成格集合 */
  blockedTiles(): Set<string> {
    const set = new Set<string>();
    const add = (o: any) => {
      if (o.active) set.add(this.tileKey(Math.floor(o.x / Bal.TILE), Math.floor(o.y / Bal.TILE)));
    };
    this.groups.walls.getChildren().forEach(add);
    this.groups.doors.getChildren().forEach(add);
    this.groups.chests.getChildren().forEach(add);
    this.groups.potions.getChildren().forEach(add);
    return set;
  }

  /** 该世界坐标是否落在可行走开放格上（非墙、非石柱） */
  isOpenAt(x: number, y: number): boolean {
    return this.openTiles.has(this.tileKey(Math.floor(x / Bal.TILE), Math.floor(y / Bal.TILE)));
  }

  /**
   * 可刷怪的开放格中心坐标。
   * `requireClearance=true` 时要求目标格四邻也开放（即 3×3 全空），
   * 以保证「比一格还大的怪物碰撞体」不会压到相邻墙体。
   */
  spawnTiles(requireClearance = true): { x: number; y: number }[] {
    const out: { x: number; y: number }[] = [];
    for (const key of this.openTiles) {
      const [gx, gy] = key.split(',').map(Number);
      if (requireClearance) {
        const nbrs = [
          this.tileKey(gx + 1, gy), this.tileKey(gx - 1, gy),
          this.tileKey(gx, gy + 1), this.tileKey(gx, gy - 1),
        ];
        if (!nbrs.every((n) => this.openTiles.has(n))) continue;
      }
      out.push({ x: gx * Bal.TILE + Bal.TILE / 2, y: gy * Bal.TILE + Bal.TILE / 2 });
    }
    return out;
  }

  /**
   * 把任意世界坐标吸附到最近的开放格中心（召唤等场景用）。
   * 若该点已在开放格上则原样返回中心；否则按环状向外搜索，
   * 保证绝不把怪物放进墙里。
   */
  snapToOpenTile(x: number, y: number, maxRadius = 8): { x: number; y: number } {
    const gx0 = Math.floor(x / Bal.TILE);
    const gy0 = Math.floor(y / Bal.TILE);
    const center = (gx: number, gy: number) => ({
      x: gx * Bal.TILE + Bal.TILE / 2,
      y: gy * Bal.TILE + Bal.TILE / 2,
    });
    if (this.openTiles.has(this.tileKey(gx0, gy0))) return center(gx0, gy0);
    for (let r = 1; r <= maxRadius; r++) {
      for (let dx = -r; dx <= r; dx++) {
        for (let dy = -r; dy <= r; dy++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const nx = gx0 + dx;
          const ny = gy0 + dy;
          if (this.openTiles.has(this.tileKey(nx, ny))) return center(nx, ny);
        }
      }
    }
    return center(gx0, gy0);
  }

  /** 找一个安全出生点（不在墙内、离门 2 格以上） */
  safePosition(): { x: number; y: number } {
    const blocked = this.blockedTiles();
    const doorTiles = this.groups.doors.getChildren().map((d: any) => ({
      x: Math.floor(d.x / Bal.TILE),
      y: Math.floor(d.y / Bal.TILE),
    }));
    for (let i = 0; i < 200; i++) {
      const gx = 1 + Math.floor(Math.random() * Math.max(1, this.w - 2));
      const gy = 1 + Math.floor(Math.random() * Math.max(1, this.h - 2));
      if (blocked.has(this.tileKey(gx, gy))) continue;
      if (doorTiles.some((d) => Math.abs(d.x - gx) + Math.abs(d.y - gy) <= 2)) continue;
      return { x: gx * Bal.TILE + Bal.TILE / 2, y: gy * Bal.TILE + Bal.TILE / 2 };
    }
    return { x: this.w * Bal.TILE / 2, y: this.h * Bal.TILE / 2 };
  }

  // ── 房间 + 走廊生成 ────────────────────────────────────────

  /**
   * 生成「房间 + 走廊」式地牢布局（经典地牢结构）：
   * 1. 随机放置若干不重叠的矩形房间（空地）
   * 2. 按中心点排序，相邻房间用 3 格宽的 L 形走廊连接，并额外连接最近邻房间形成环路
   * 3. 房间与走廊之外全部为实心岩石墙 → 形成真正「有房间」的地图，而非散乱单格墙
   * 4. 石柱点缀在 build() 中于内容放置后添加（此处只产出布局）
   *
   * 返回 { 开放格, 岩石墙格, 房间列表 }。
   */
  private generateLayout(floor: number): Layout {
    const { w, h } = this;
    const allTiles = new Set<string>();

    // 收集所有可放置房间的格子（留出边界 1 格）
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        allTiles.add(this.tileKey(x, y));
      }
    }

    // 决定房间数量（小图少、大图多）
    const mapArea = w * h;
    const roomCount = Math.min(
      ROOM_MAX_COUNT,
      Math.max(ROOM_MIN_COUNT, Math.round(mapArea / 150))
    );

    // 生成房间
    const rooms: Room[] = [];
    const occupied = new Set<string>();

    for (let attempt = 0; attempt < roomCount * 50 && rooms.length < roomCount; attempt++) {
      const rw = ROOM_MIN_SIZE + Math.floor(Math.random() * (ROOM_MAX_SIZE - ROOM_MIN_SIZE + 1));
      const rh = ROOM_MIN_SIZE + Math.floor(Math.random() * (ROOM_MAX_SIZE - ROOM_MIN_SIZE + 1));
      const rx = 1 + Math.floor(Math.random() * Math.max(1, w - rw - 2));
      const ry = 1 + Math.floor(Math.random() * Math.max(1, h - rh - 2));

      // 检查是否与现有房间重叠（留 1 格间距）
      let overlap = false;
      for (const key of occupied) {
        const [ox, oy] = key.split(',').map(Number);
        if (rx - 1 <= ox && rx + rw + 1 > ox && ry - 1 <= oy && ry + rh + 1 > oy) {
          overlap = true;
          break;
        }
      }
      if (overlap) continue;

      const room: Room = {
        x: rx, y: ry, w: rw, h: rh,
        cx: rx + Math.floor(rw / 2),
        cy: ry + Math.floor(rh / 2),
      };

      // 标记房间占用的格子
      for (let dy = 0; dy < rh; dy++) {
        for (let dx = 0; dx < rw; dx++) {
          occupied.add(this.tileKey(rx + dx, ry + dy));
        }
      }
      rooms.push(room);
    }

    // 退化：房间太少 → 用随机单格墙（开放格 = 其余全部）
    if (rooms.length < 2) {
      const wallTiles = this.generateRandomWalls(floor);
      const openTiles = new Set<string>();
      allTiles.forEach((k) => { if (!wallTiles.has(k)) openTiles.add(k); });
      return { openTiles, wallTiles, rooms };
    }

    // 按中心点排序（用于走廊连接）
    rooms.sort((a, b) => (a.cx + a.cy) - (b.cx + b.cy));

    // 连接相邻房间：用 3 格宽 L 形走廊
    const corridorTiles = new Set<string>();
    for (let i = 0; i < rooms.length - 1; i++) {
      this.traceCorridor(rooms[i], rooms[i + 1], corridorTiles);
    }

    // 额外连接：每个房间连到最近的两个邻居，形成环路（增加通路、减少死路）
    const linked = new Set<string>();
    for (let i = 0; i < rooms.length; i++) {
      const order = rooms
        .map((r, j) => ({ j, d: Math.abs(rooms[i].cx - r.cx) + Math.abs(rooms[i].cy - r.cy) }))
        .filter((o) => o.j !== i)
        .sort((a, b) => a.d - b.d);
      for (const o of order.slice(0, 2)) {
        const key = i < o.j ? `${i}-${o.j}` : `${o.j}-${i}`;
        if (linked.has(key)) continue;
        linked.add(key);
        this.traceCorridor(rooms[i], rooms[o.j], corridorTiles);
      }
    }

    // 空地 = 房间 ∪ 走廊；其余内部格全部为岩石墙（真正的地牢结构）
    const openTiles = new Set<string>(occupied);
    corridorTiles.forEach((t) => openTiles.add(t));

    const wallTiles = new Set<string>();
    for (const key of allTiles) {
      if (!openTiles.has(key)) wallTiles.add(key);
    }

    return { openTiles, wallTiles, rooms };
  }

  /**
   * 从开放格中挑一个未被占用、且满足距离约束的格子。
   * 优先房间内的开放格（视觉自然、四周空间充足），房间池不足时回退到任意开放格。
   */
  private pickOpenTile(
    roomList: string[],
    openList: string[],
    used: Set<string>,
    farFrom?: { x: number; y: number },
    minDist = 0
  ): { x: number; y: number } {
    const tryPool = (pool: string[]): { x: number; y: number } | null => {
      if (pool.length === 0) return null;
      for (let t = 0; t < 200; t++) {
        const k = pool[Math.floor(Math.random() * pool.length)];
        if (used.has(k)) continue;
        const [gx, gy] = k.split(',').map(Number);
        if (farFrom) {
          const d = Math.abs(farFrom.x - gx) + Math.abs(farFrom.y - gy);
          if (d < minDist) continue;
        }
        return { x: gx, y: gy };
      }
      return null;
    };

    const fromRooms = tryPool(roomList);
    if (fromRooms) return fromRooms;
    const fromOpen = tryPool(openList);
    if (fromOpen) return fromOpen;

    // 兜底：忽略距离约束，取任意开放格
    for (const k of openList) {
      if (!used.has(k)) {
        const [gx, gy] = k.split(',').map(Number);
        return { x: gx, y: gy };
      }
    }
    return { x: Math.floor(this.w / 2), y: Math.floor(this.h / 2) };
  }

  /**
   * 在房间内点缀石柱作为掩体。
   * 只放置「四邻皆为空地」且「不与已放置石柱相邻」的孤立格 → 
   * 既提供掩体，又绝不可能阻断通行（连通性有保证）。
   * `blocked` 为已占位（门/宝箱/药水）的格子，石柱不会覆盖它们。
   */
  private addRoomPillars(
    rooms: Room[],
    openTiles: Set<string>,
    wallTiles: Set<string>,
    density: number,
    blocked: Set<string>
  ) {
    const candidates: string[] = [];
    for (const r of rooms) {
      // 距房间边界 1 格以上，避免堵住门口
      for (let dy = 1; dy < r.h - 1; dy++) {
        for (let dx = 1; dx < r.w - 1; dx++) {
          const key = this.tileKey(r.x + dx, r.y + dy);
          if (blocked.has(key) || !openTiles.has(key)) continue;
          const nbrs = [
            this.tileKey(r.x + dx + 1, r.y + dy),
            this.tileKey(r.x + dx - 1, r.y + dy),
            this.tileKey(r.x + dx, r.y + dy + 1),
            this.tileKey(r.x + dx, r.y + dy - 1),
          ];
          // 必须四邻皆空（且未被占位），才可能是孤立石柱
          if (nbrs.every((n) => openTiles.has(n) && !blocked.has(n))) candidates.push(key);
        }
      }
    }

    // 洗牌
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }

    const target = Math.floor(rooms.reduce((s, r) => s + r.w * r.h, 0) * density);
    let placed = 0;
    const placedSet = new Set<string>();
    for (const key of candidates) {
      if (placed >= target) break;
      const [x, y] = key.split(',').map(Number);
      // 不与已放置石柱相邻（保持孤立，绝不形成阻断墙）
      const adj = [
        this.tileKey(x + 1, y), this.tileKey(x - 1, y),
        this.tileKey(x, y + 1), this.tileKey(x, y - 1),
      ];
      if (adj.some((a) => placedSet.has(a))) continue;
      placedSet.add(key);
      openTiles.delete(key);
      wallTiles.add(key);
      placed++;
    }
  }

  /** 连接两个房间的 L 形走廊 */
  private traceCorridor(
    from: Room,
    to: Room,
    corridorTiles: Set<string>
  ) {
    const hw = CORRIDOR_WIDTH; // 走廊半宽

    // 先水平再垂直（或相反，随机选择）
    const horizontalFirst = Math.random() > 0.5;

    if (horizontalFirst) {
      this.traceCorridorSegment(
        from.cx, from.cy, to.cx, from.cy,
        corridorTiles, hw
      );
      this.traceCorridorSegment(
        to.cx, from.cy, to.cx, to.cy,
        corridorTiles, hw
      );
    } else {
      this.traceCorridorSegment(
        from.cx, from.cy, from.cx, to.cy,
        corridorTiles, hw
      );
      this.traceCorridorSegment(
        from.cx, to.cy, to.cx, to.cy,
        corridorTiles, hw
      );
    }
  }

  /** 追踪一段直线走廊（带宽度） */
  private traceCorridorSegment(
    x1: number, y1: number,
    x2: number, y2: number,
    corridorTiles: Set<string>,
    hw: number
  ) {
    const dx = Math.sign(x2 - x1);
    const dy = Math.sign(y2 - y1);
    let x = x1, y = y1;

    while (x !== x2 || y !== y2) {
      // 标记走廊宽度范围
      for (let ox = -hw; ox <= hw; ox++) {
        for (let oy = -hw; oy <= hw; oy++) {
          const nx = x + ox;
          const ny = y + oy;
          if (nx >= 0 && nx < this.w && ny >= 0 && ny < this.h) {
            corridorTiles.add(this.tileKey(nx, ny));
          }
        }
      }
      x += dx;
      y += dy;
    }

    // 最后一格
    for (let ox = -hw; ox <= hw; ox++) {
      for (let oy = -hw; oy <= hw; oy++) {
        const nx = x + ox;
        const ny = y + oy;
        if (nx >= 0 && nx < this.w && ny >= 0 && ny < this.h) {
          corridorTiles.add(this.tileKey(nx, ny));
        }
      }
    }
  }

  /** 退化的随机单格墙生成（房间太少时使用） */
  private generateRandomWalls(floor: number): Set<string> {
    const { w, h } = this;
    const wallTiles = new Set<string>();
    const wallCount = Bal.interiorWallCount(floor, w, h);

    for (let i = 0; i < wallCount; i++) {
      const wx = 2 + Math.floor(Math.random() * Math.max(1, w - 4));
      const wy = 2 + Math.floor(Math.random() * Math.max(1, h - 4));
      wallTiles.add(this.tileKey(wx, wy));
    }
    return wallTiles;
  }

  // ── 内部 ──────────────────────────────────────────────────

  private createWall(x: number, y: number): Wall {
    // 关键：墙放在「格子中心」(t*TILE + TILE/2)，与玩家/敌人/地板网格对齐。
    // 若放在 t*TILE（格子角落），整张地图会偏离半格，
    // 导致敌人在逻辑空地上生成时压到视觉上偏移过来的墙。
    const wall = new Wall(this.scene, this.px(x), this.px(y), 'wall');
    this.groups.walls.add(wall);
    return wall;
  }

  private createDoor(x: number, y: number, dir: DoorDirection): Door {
    const door = new Door(this.scene, this.px(x), this.px(y), dir);
    this.groups.doors.add(door);
    if (dir === 'down') this.downDoor = door;

    const label = this.scene.add
      .text(this.px(x), this.px(y) - 18, dir === 'up' ? tr('door.up') : tr('door.down'), {
        fontSize: '12px',
        fontFamily: 'monospace',
        color: dir === 'up' ? UI.doorUp : UI.doorDown,
      })
      .setOrigin(0.5)
      .setDepth(DEPTH.worldLabel);
    this.doorLabels.push(label);
    return door;
  }

  private createChest(x: number, y: number): Chest {
    const chest = new Chest(this.scene, this.px(x), this.px(y), 'chest');
    this.groups.chests.add(chest);
    return chest;
  }

  private createPotion(x: number, y: number): Potion {
    const potion = new Potion(this.scene, this.px(x), this.px(y), 'potion');
    this.groups.potions.add(potion);
    return potion;
  }
}
